<template>
  <div class="equator-game">
    <div class="header">
      <h1>🧮 Equator - Equation Solver</h1>
      <p>Drag equation components to solve! Move terms across the equals sign to isolate x.</p>
    </div>

    <div class="game-content">
      <!-- Game Stats -->
      <div class="stats-panel">
        <div class="stat-item">
          <span class="stat-label">Level</span>
          <span class="stat-value">{{ currentLevel }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Score</span>
          <span class="stat-value">{{ score }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Moves</span>
          <span class="stat-value">{{ moves }}</span>
        </div>
      </div>

      <!-- Equation Workspace -->
      <div class="equation-workspace">
        <div class="workspace-title">Solve for x:</div>
        
        <!-- Left Side of Equation -->
        <div class="equation-side left-side">
          <div 
            v-for="(term, index) in leftSide" 
            :key="'left-' + index"
            class="term-container"
            :class="{ 'dragging': draggingTerm && draggingTerm.side === 'left' && draggingTerm.index === index }"
          >
            <div 
              class="equation-term"
              :class="{ 
                'coefficient': term.type === 'coefficient',
                'variable': term.type === 'variable',
                'constant': term.type === 'constant',
                'operator': term.type === 'operator',
                'draggable': term.draggable
              }"
              :draggable="term.draggable"
              @click="term.draggable ? moveTerm('left', index, 'right') : null"
              @dragstart="startDrag($event, 'left', index)"
              @dragend="endDrag"
              @touchstart="touchStart($event, 'left', index)"
              @touchmove="touchMove"
              @touchend="touchEnd"
            >
              {{ term.display }}
            </div>
          </div>
        </div>

        <!-- Equals Sign -->
        <div class="equals-sign">=</div>

        <!-- Right Side of Equation -->
        <div class="equation-side right-side">
          <div 
            v-for="(term, index) in rightSide" 
            :key="'right-' + index"
            class="term-container"
            :class="{ 'dragging': draggingTerm && draggingTerm.side === 'right' && draggingTerm.index === index }"
          >
            <div 
              class="equation-term"
              :class="{ 
                'coefficient': term.type === 'coefficient',
                'variable': term.type === 'variable',
                'constant': term.type === 'constant',
                'operator': term.type === 'operator',
                'draggable': term.draggable
              }"
              :draggable="term.draggable"
              @click="term.draggable ? moveTerm('right', index, 'left') : null"
              @dragstart="startDrag($event, 'right', index)"
              @dragend="endDrag"
              @touchstart="touchStart($event, 'right', index)"
              @touchmove="touchMove"
              @touchend="touchEnd"
            >
              {{ term.display }}
            </div>
          </div>
        </div>
      </div>

      <!-- Drop Zones -->
      <div class="drop-zones">
        <div 
          class="drop-zone"
          @dragover.prevent
          @drop="dropOnSide($event, 'left')"
          @dragenter="highlightZone('left')"
          @dragleave="unhighlightZone('left')"
          :class="{ 'highlight': highlightedZone === 'left' }"
        >
          <span class="zone-label">← Move to Left Side</span>
        </div>
        <div 
          class="drop-zone"
          @dragover.prevent
          @drop="dropOnSide($event, 'right')"
          @dragenter="highlightZone('right')"
          @dragleave="unhighlightZone('right')"
          :class="{ 'highlight': highlightedZone === 'right' }"
        >
          <span class="zone-label">Move to Right Side →</span>
        </div>
      </div>

      <!-- Simplified Equation Display -->
      <div class="simplified-display">
        <div class="simplified-label">Simplified:</div>
        <div class="simplified-equation">{{ simplifiedEquation }}</div>
      </div>

      <!-- Action Buttons -->
      <div class="action-buttons">
        <button @click="checkSolution" class="btn btn-check">Check Solution</button>
        <button @click="resetEquation" class="btn btn-reset">Reset</button>
        <button @click="getHint" class="btn btn-hint">Hint</button>
      </div>

      <!-- Hint Display -->
      <div v-if="hint" class="hint-display">
        <span class="hint-icon">💡</span>
        <span class="hint-text">{{ hint }}</span>
      </div>

      <!-- Success Message -->
      <transition name="fade">
        <div v-if="showSuccess" class="success-message">
          <div class="success-content">
            <div class="success-icon">🎉</div>
            <div class="success-text">Correct! x = {{ solution }}</div>
            <div class="success-stats">Solved in {{ moves }} moves!</div>
            <button @click="nextLevel" class="btn btn-next">Next Level</button>
          </div>
        </div>
      </transition>
    </div>

    <!-- Instructions Panel -->
    <div class="instructions-panel">
      <h3>How to Play:</h3>
      <ul>
        <li><strong>Goal:</strong> Isolate x on one side of the equation</li>
        <li><strong>Drag terms</strong> across the equals sign to move them</li>
        <li>When you move a term, its sign flips (+ becomes -, × becomes ÷)</li>
        <li><strong>Coefficients:</strong> Drag the number away from x to divide both sides</li>
        <li><strong>Simplify:</strong> Combine like terms automatically</li>
        <li><strong>Win:</strong> Get x alone on one side!</li>
      </ul>
    </div>

    <!-- SEO Content -->
    <div class="seo-content-section">
      <div class="seo-scrollview">
        <div class="seo-content">
          <h2>Equator - Interactive Equation Solving Game</h2>
          
          <h3>What is Equator?</h3>
          <p>Equator is an innovative educational math game that transforms abstract algebra into an interactive, hands-on experience. Players learn to solve linear equations by physically manipulating equation components, making algebra intuitive and fun. This game is perfect for students learning algebra, teachers looking for engaging educational tools, and anyone who wants to improve their equation-solving skills.</p>
          
          <h3>How to Play Equator</h3>
          <p>The gameplay is simple yet powerful: drag equation terms across the equals sign to move them between sides. When you move a term, mathematical operations automatically adjust - addition becomes subtraction, multiplication becomes division. The game features progressive difficulty levels, starting with simple one-step equations like "x + 5 = 8" and advancing to complex multi-step equations with coefficients and multiple terms.</p>
          
          <h3>Educational Benefits</h3>
          <p>Equator teaches fundamental algebra concepts through interactive gameplay. Students develop deep understanding of equation balance, inverse operations, and algebraic manipulation. The visual, kinesthetic approach helps learners who struggle with traditional textbook methods. Each move reinforces the principle that equations must remain balanced, making abstract concepts concrete and memorable.</p>
          
          <h3>Game Features</h3>
          <p>The game includes multiple difficulty levels, real-time equation simplification, hint system for stuck players, move counter to track efficiency, and progressive challenges that introduce new concepts gradually. The intuitive drag-and-drop interface works on both desktop and mobile devices, making algebra practice accessible anywhere.</p>
          
          <h3>Why Play Equator?</h3>
          <p>Traditional algebra instruction can feel abstract and disconnected from real understanding. Equator bridges this gap by making equation solving a tactile, visual experience. Players gain confidence as they progress through levels, building mathematical intuition that transfers to paper-and-pencil problem solving. The game rewards both speed and accuracy, encouraging players to find elegant solutions.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'EquatorGame',
  data() {
    return {
      currentLevel: 1,
      score: 0,
      moves: 0,
      leftSide: [],
      rightSide: [],
      draggingTerm: null,
      highlightedZone: null,
      hint: '',
      showSuccess: false,
      solution: 0,
      touchStartPos: null,
      touchCurrentTerm: null,
      
      // Comprehensive equation pool - randomly selected for each game
      equationPool: [
        // Easy: One-step addition/subtraction (x ± a = b)
        { left: [{ val: 1, var: 'x' }, { val: 5, var: '' }], right: [{ val: 8, var: '' }], answer: 3 },
        { left: [{ val: 1, var: 'x' }, { val: -3, var: '' }], right: [{ val: 7, var: '' }], answer: 10 },
        { left: [{ val: 1, var: 'x' }, { val: 7, var: '' }], right: [{ val: 12, var: '' }], answer: 5 },
        { left: [{ val: 1, var: 'x' }, { val: -4, var: '' }], right: [{ val: 6, var: '' }], answer: 10 },
        { left: [{ val: 1, var: 'x' }, { val: 9, var: '' }], right: [{ val: 15, var: '' }], answer: 6 },
        { left: [{ val: 1, var: 'x' }, { val: -8, var: '' }], right: [{ val: 2, var: '' }], answer: 10 },
        { left: [{ val: 1, var: 'x' }, { val: 6, var: '' }], right: [{ val: 14, var: '' }], answer: 8 },
        { left: [{ val: 1, var: 'x' }, { val: -2, var: '' }], right: [{ val: 9, var: '' }], answer: 11 },
        
        // Easy: One-step multiplication/division (ax = b)
        { left: [{ val: 2, var: 'x' }], right: [{ val: 10, var: '' }], answer: 5 },
        { left: [{ val: 3, var: 'x' }], right: [{ val: 12, var: '' }], answer: 4 },
        { left: [{ val: 4, var: 'x' }], right: [{ val: 20, var: '' }], answer: 5 },
        { left: [{ val: 5, var: 'x' }], right: [{ val: 25, var: '' }], answer: 5 },
        { left: [{ val: 6, var: 'x' }], right: [{ val: 18, var: '' }], answer: 3 },
        { left: [{ val: 7, var: 'x' }], right: [{ val: 21, var: '' }], answer: 3 },
        { left: [{ val: 8, var: 'x' }], right: [{ val: 32, var: '' }], answer: 4 },
        { left: [{ val: 9, var: 'x' }], right: [{ val: 27, var: '' }], answer: 3 },
        
        // Medium: Two-step equations (ax + b = c)
        { left: [{ val: 3, var: 'x' }, { val: 4, var: '' }], right: [{ val: 13, var: '' }], answer: 3 },
        { left: [{ val: 2, var: 'x' }, { val: -5, var: '' }], right: [{ val: 9, var: '' }], answer: 7 },
        { left: [{ val: 4, var: 'x' }, { val: 3, var: '' }], right: [{ val: 19, var: '' }], answer: 4 },
        { left: [{ val: 5, var: 'x' }, { val: -2, var: '' }], right: [{ val: 13, var: '' }], answer: 3 },
        { left: [{ val: 3, var: 'x' }, { val: 7, var: '' }], right: [{ val: 22, var: '' }], answer: 5 },
        { left: [{ val: 2, var: 'x' }, { val: 8, var: '' }], right: [{ val: 16, var: '' }], answer: 4 },
        { left: [{ val: 6, var: 'x' }, { val: -4, var: '' }], right: [{ val: 14, var: '' }], answer: 3 },
        { left: [{ val: 4, var: 'x' }, { val: 5, var: '' }], right: [{ val: 21, var: '' }], answer: 4 },
        { left: [{ val: 7, var: 'x' }, { val: -3, var: '' }], right: [{ val: 18, var: '' }], answer: 3 },
        { left: [{ val: 3, var: 'x' }, { val: -6, var: '' }], right: [{ val: 12, var: '' }], answer: 6 },
        
        // Medium-Hard: Variables on both sides (ax + b = cx + d)
        { left: [{ val: 5, var: 'x' }, { val: 3, var: '' }], right: [{ val: 2, var: 'x' }, { val: 12, var: '' }], answer: 3 },
        { left: [{ val: 4, var: 'x' }, { val: -7, var: '' }], right: [{ val: 1, var: 'x' }, { val: 8, var: '' }], answer: 5 },
        { left: [{ val: 6, var: 'x' }, { val: 2, var: '' }], right: [{ val: 3, var: 'x' }, { val: 14, var: '' }], answer: 4 },
        { left: [{ val: 7, var: 'x' }, { val: -5, var: '' }], right: [{ val: 4, var: 'x' }, { val: 10, var: '' }], answer: 5 },
        { left: [{ val: 8, var: 'x' }, { val: 3, var: '' }], right: [{ val: 5, var: 'x' }, { val: 15, var: '' }], answer: 4 },
        { left: [{ val: 9, var: 'x' }, { val: -4, var: '' }], right: [{ val: 6, var: 'x' }, { val: 8, var: '' }], answer: 4 },
        { left: [{ val: 10, var: 'x' }, { val: 5, var: '' }], right: [{ val: 7, var: 'x' }, { val: 14, var: '' }], answer: 3 },
        { left: [{ val: 6, var: 'x' }, { val: -8, var: '' }], right: [{ val: 2, var: 'x' }, { val: 4, var: '' }], answer: 3 },
        { left: [{ val: 5, var: 'x' }, { val: 6, var: '' }], right: [{ val: 3, var: 'x' }, { val: 16, var: '' }], answer: 5 },
        { left: [{ val: 8, var: 'x' }, { val: -2, var: '' }], right: [{ val: 3, var: 'x' }, { val: 13, var: '' }], answer: 3 },
        
        // Hard: More complex two-sided equations
        { left: [{ val: 12, var: 'x' }, { val: -7, var: '' }], right: [{ val: 8, var: 'x' }, { val: 9, var: '' }], answer: 4 },
        { left: [{ val: 9, var: 'x' }, { val: 5, var: '' }], right: [{ val: 4, var: 'x' }, { val: 20, var: '' }], answer: 3 },
        { left: [{ val: 11, var: 'x' }, { val: -6, var: '' }], right: [{ val: 7, var: 'x' }, { val: 10, var: '' }], answer: 4 },
        { left: [{ val: 10, var: 'x' }, { val: 8, var: '' }], right: [{ val: 6, var: 'x' }, { val: 20, var: '' }], answer: 3 },
        { left: [{ val: 13, var: 'x' }, { val: -9, var: '' }], right: [{ val: 9, var: 'x' }, { val: 7, var: '' }], answer: 4 },
        { left: [{ val: 15, var: 'x' }, { val: 4, var: '' }], right: [{ val: 10, var: 'x' }, { val: 19, var: '' }], answer: 3 },
        { left: [{ val: 14, var: 'x' }, { val: -5, var: '' }], right: [{ val: 9, var: 'x' }, { val: 15, var: '' }], answer: 4 },
        { left: [{ val: 12, var: 'x' }, { val: 6, var: '' }], right: [{ val: 7, var: 'x' }, { val: 21, var: '' }], answer: 3 },
        
        // Additional variety
        { left: [{ val: 2, var: 'x' }, { val: 3, var: '' }], right: [{ val: 11, var: '' }], answer: 4 },
        { left: [{ val: 5, var: 'x' }, { val: -7, var: '' }], right: [{ val: 18, var: '' }], answer: 5 },
        { left: [{ val: 3, var: 'x' }, { val: 9, var: '' }], right: [{ val: 24, var: '' }], answer: 5 },
        { left: [{ val: 4, var: 'x' }, { val: -6, var: '' }], right: [{ val: 10, var: '' }], answer: 4 },
        { left: [{ val: 6, var: 'x' }, { val: 5, var: '' }], right: [{ val: 29, var: '' }], answer: 4 },
        { left: [{ val: 7, var: 'x' }, { val: -8, var: '' }], right: [{ val: 13, var: '' }], answer: 3 },
        { left: [{ val: 8, var: 'x' }, { val: 7, var: '' }], right: [{ val: 39, var: '' }], answer: 4 },
        { left: [{ val: 9, var: 'x' }, { val: -10, var: '' }], right: [{ val: 17, var: '' }], answer: 3 },
        
        // More challenging
        { left: [{ val: 11, var: 'x' }, { val: 3, var: '' }], right: [{ val: 6, var: 'x' }, { val: 18, var: '' }], answer: 3 },
        { left: [{ val: 13, var: 'x' }, { val: -4, var: '' }], right: [{ val: 8, var: 'x' }, { val: 16, var: '' }], answer: 4 },
        { left: [{ val: 16, var: 'x' }, { val: 5, var: '' }], right: [{ val: 11, var: 'x' }, { val: 25, var: '' }], answer: 4 },
        { left: [{ val: 17, var: 'x' }, { val: -7, var: '' }], right: [{ val: 12, var: 'x' }, { val: 13, var: '' }], answer: 4 },
        { left: [{ val: 20, var: 'x' }, { val: 8, var: '' }], right: [{ val: 15, var: 'x' }, { val: 23, var: '' }], answer: 3 },
      ],
      
      // Selected levels for current game session (randomly picked)
      levels: [],
      usedEquations: new Set()
    };
  },
  
  computed: {
    simplifiedEquation() {
      const leftStr = this.formatSide(this.leftSide);
      const rightStr = this.formatSide(this.rightSide);
      return `${leftStr} = ${rightStr}`;
    }
  },
  
  mounted() {
    // Initialize with random equations
    this.shuffleEquations();
    this.loadLevel(this.currentLevel);
  },
  
  methods: {
    shuffleEquations() {
      // Create a shuffled copy of the equation pool
      const shuffled = [...this.equationPool];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      this.levels = shuffled;
      this.usedEquations.clear();
    },
    loadLevel(level) {
      const levelData = this.levels[level - 1];
      if (!levelData) {
        alert('Congratulations! You completed all levels!');
        return;
      }
      
      this.leftSide = this.parseTerms(levelData.left);
      this.rightSide = this.parseTerms(levelData.right);
      this.solution = levelData.answer;
      this.moves = 0;
      this.hint = '';
      this.showSuccess = false;
    },
    
    parseTerms(terms) {
      const result = [];
      
      terms.forEach((term, idx) => {
        // Add operator if not first term and value is positive
        if (idx > 0 && term.val >= 0) {
          result.push({
            type: 'operator',
            display: '+',
            draggable: false,
            value: 0,
            variable: ''
          });
        }
        
        const absVal = Math.abs(term.val);
        
        if (term.var === 'x') {
          // Variable term
          if (absVal !== 1) {
            // Coefficient
            result.push({
              type: 'coefficient',
              display: absVal.toString(),
              draggable: true,
              value: absVal,
              variable: '',
              isCoefficient: true
            });
          }
          // Variable
          result.push({
            type: 'variable',
            display: 'x',
            draggable: true,
            value: term.val, // Keep original value for x coefficient
            variable: 'x',
            isVariable: true
          });
        } else {
          // Constant term
          result.push({
            type: 'constant',
            display: term.val.toString(), // Display actual value including sign
            draggable: true,
            value: term.val,
            variable: ''
          });
        }
      });
      
      return result;
    },
    
    formatSide(side) {
      if (side.length === 0) return '0';
      
      let str = '';
      
      for (let i = 0; i < side.length; i++) {
        const term = side[i];
        const prevTerm = i > 0 ? side[i-1] : null;
        
        if (term.type === 'operator') {
          str += ` ${term.display} `;
        } else if (term.type === 'coefficient') {
          // Check if associated variable is negative to add minus sign
          const nextTerm = i < side.length - 1 ? side[i+1] : null;
          if (nextTerm && nextTerm.type === 'variable' && nextTerm.value < 0) {
            str += '-';
          }
          str += term.display;
        } else if (term.type === 'variable') {
          // Only display 'x', value is handled by coefficient or sign
          if (prevTerm && prevTerm.type === 'coefficient') {
            str += 'x';
          } else {
            // Standalone variable (e.g. x or -x)
            if (term.value === -1) {
              str += '-';
            }
            str += 'x';
          }
        } else if (term.type === 'constant') {
          str += term.display;
        }
      }
      
      return str.replace(/\s+/g, ' ').trim() || '0';
    },
    
    startDrag(event, side, index) {
      const term = side === 'left' ? this.leftSide[index] : this.rightSide[index];
      if (!term.draggable) {
        event.preventDefault();
        return;
      }
      
      this.draggingTerm = { side, index, term };
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', JSON.stringify({ side, index }));
    },
    
    endDrag() {
      this.draggingTerm = null;
      this.highlightedZone = null;
    },
    
    touchStart(event, side, index) {
      const term = side === 'left' ? this.leftSide[index] : this.rightSide[index];
      if (!term.draggable) return;
      
      this.touchCurrentTerm = { side, index, term };
      const touch = event.touches[0];
      this.touchStartPos = { x: touch.clientX, y: touch.clientY };
      // Prevent default to avoid scrolling, but allow click to register later
      // event.preventDefault(); 
    },
    
    touchMove(event) {
      if (!this.touchCurrentTerm) return;
      const touch = event.touches[0];
      const deltaX = touch.clientX - this.touchStartPos.x;
      const deltaY = touch.clientY - this.touchStartPos.y;

      // If significant movement, consider it a drag, prevent default to stop scrolling
      if (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10) {
        event.preventDefault();
      }
    },
    
    touchEnd(event) {
      if (!this.touchCurrentTerm) return;
      
      const touch = event.changedTouches[0];
      const endX = touch.clientX;
      
      // Determine if moved left or right significantly
      const deltaX = endX - this.touchStartPos.x;
      
      if (Math.abs(deltaX) > 50) { // Threshold for horizontal drag
        const targetSide = this.touchCurrentTerm.side === 'left' ? 'right' : 'left'; // Always move to opposite side on horizontal drag
        this.moveTerm(this.touchCurrentTerm.side, this.touchCurrentTerm.index, targetSide);
      } else {
        // If it was a tap/click, trigger clickTerm
        this.clickTerm(this.touchCurrentTerm.side, this.touchCurrentTerm.index);
      }
      
      this.touchCurrentTerm = null;
      this.touchStartPos = null;
    },

    clickTerm(sourceSide, sourceIndex) {
      const term = sourceSide === 'left' ? this.leftSide[sourceIndex] : this.rightSide[sourceIndex];
      if (!term.draggable) return;

      // Determine target side (always opposite for click-to-move)
      const targetSide = sourceSide === 'left' ? 'right' : 'left';
      this.moveTerm(sourceSide, sourceIndex, targetSide);
    },
    
    highlightZone(zone) {
      this.highlightedZone = zone;
    },
    
    unhighlightZone(zone) {
      if (this.highlightedZone === zone) {
        this.highlightedZone = null;
      }
    },
    
    dropOnSide(event, targetSide) {
      event.preventDefault();
      this.highlightedZone = null;
      
      const data = event.dataTransfer.getData('text/plain');
      if (!data) return;

      const { side: sourceSide, index: sourceIndex } = JSON.parse(data);
      
      if (sourceSide === targetSide) return; // No move if dropped on same side
      
      this.moveTerm(sourceSide, sourceIndex, targetSide);
    },
    
    moveTerm(sourceSide, sourceIndex, targetSide) {
      this.moves++;
      
      const sourceArray = sourceSide === 'left' ? this.leftSide : this.rightSide;
      const targetArray = targetSide === 'left' ? this.leftSide : this.rightSide;
      
      const termToMove = sourceArray[sourceIndex];
      
      // Handle coefficient movement (division)
      if (termToMove.isCoefficient) {
        // Find the actual variable term associated with this coefficient
        const variableTermIndex = sourceIndex + 1; // Assuming variable is right after coefficient
        if (variableTermIndex < sourceArray.length && sourceArray[variableTermIndex].isVariable) {
          this.divideByCoefficient(termToMove.value);
          return;
        }
      }
      
      // Create a deep copy of the term to move
      const movedTerm = JSON.parse(JSON.stringify(termToMove));
      
      // Remove term and its preceding operator from source array
      let removeCount = 1;
      let removeStart = sourceIndex;
      
      if (sourceIndex > 0 && sourceArray[sourceIndex - 1].type === 'operator') {
        removeStart = sourceIndex - 1;
        removeCount = 2;
      }
      
      sourceArray.splice(removeStart, removeCount);
      
      // Flip the sign for constants and variable coefficients
      if (movedTerm.type === 'constant' || movedTerm.type === 'variable') {
        movedTerm.value = -movedTerm.value;
        movedTerm.display = movedTerm.value.toString();
        if (movedTerm.isVariable) {
          movedTerm.display = 'x'; // Display 'x' for variable terms
        }
      }
      
      // Add to target side, ensuring correct operator placement
      if (targetArray.length > 0) {
        const lastTermInTarget = targetArray[targetArray.length - 1];
        if (lastTermInTarget.type === 'operator') {
          // If last term is an operator, replace it or adjust
          if (movedTerm.value >= 0) {
            lastTermInTarget.display = '+';
          } else {
            lastTermInTarget.display = '-';
          }
          targetArray.push(movedTerm);
        } else {
          // Add new operator before the term
          const sign = movedTerm.value >= 0 ? '+' : '-';
          targetArray.push({
            type: 'operator',
            display: sign,
            draggable: false,
            value: 0,
            variable: ''
          });
          targetArray.push(movedTerm);
        }
      } else {
        // If target array is empty, just add the term
        targetArray.push(movedTerm);
      }
      
      // Simplify both sides after the move
      this.simplifySides();
      
      // Check if solved
      this.checkIfSolved();
    },
    
    divideByCoefficient(coefficient) {

      
      // Apply division to all terms on both sides
      const divideTerms = (terms) => {
        const newTerms = [];
        for (let i = 0; i < terms.length; i++) {
          const term = terms[i];
          
          if (term.isCoefficient) {
            // Skip the coefficient itself - it's being divided out
            // The next term should be the variable, which we'll keep but update
            continue;
          } else if (term.isVariable) {
            // Keep the variable but update its coefficient value
            const newValue = term.value / coefficient;
            newTerms.push({
              ...term,
              value: newValue,
              display: 'x'
            });
          } else if (term.type === 'constant') {
            // Divide constants by the coefficient
            const newValue = term.value / coefficient;
            newTerms.push({
              ...term,
              value: newValue,
              display: Math.abs(newValue).toString()
            });
          } else if (term.type === 'operator') {
            // Keep operators as-is
            newTerms.push(term);
          }
        }
        return newTerms;
      };

      this.leftSide = divideTerms(this.leftSide);
      this.rightSide = divideTerms(this.rightSide);
      
      this.simplifySides();
      this.checkIfSolved();
    },
    
    simplifySides() {
      this.leftSide = this.combineTerms(this.leftSide);
      this.rightSide = this.combineTerms(this.rightSide);
    },
    
    combineTerms(terms) {
      let xCoeff = 0;
      let constant = 0;
      
      terms.forEach(term => {
        if (term.type === 'variable' || term.isVariable) {
          xCoeff += term.value;
        } else if (term.type === 'constant') {
          constant += term.value;
        }
        // Coefficients are handled by being part of the variable term's value
        // or explicitly removed during division.
      });
      
      const result = [];
      
      if (xCoeff !== 0) {
        const absCoeff = Math.abs(xCoeff);
        if (absCoeff !== 1) {
          result.push({
            type: 'coefficient',
            display: parseFloat(absCoeff.toFixed(2)).toString(),
            draggable: true,
            value: absCoeff,
            variable: '',
            isCoefficient: true
          });
        }
        result.push({
          type: 'variable',
          display: 'x',
          draggable: true,
          value: xCoeff, // Store the actual coefficient value here
          variable: 'x',
          isVariable: true
        });
      }
      
      if (constant !== 0) {
        if (result.length > 0 && constant > 0) { // Only add '+' if there's a preceding term and constant is positive
          result.push({
            type: 'operator',
            display: '+',
            draggable: false,
            value: 0,
            variable: ''
          });
        }
        result.push({
          type: 'constant',
          display: parseFloat(constant.toFixed(2)).toString(), // Display actual value including sign
          draggable: true,
          value: constant,
          variable: ''
        });
      }
      
      return result;
    },
    
    checkIfSolved() {
      // Check if x is isolated on one side and equals a constant on the other
      const leftHasX = this.leftSide.some(t => t.isVariable);
      const rightHasX = this.rightSide.some(t => t.isVariable);
      
      // x should be on one side only
      if (leftHasX && rightHasX) return false;
      if (!leftHasX && !rightHasX) return false; // No x at all
      
      const xSide = leftHasX ? this.leftSide : this.rightSide;
      const valueSide = leftHasX ? this.rightSide : this.leftSide;
      
      // x side should only have 'x' (possibly with coefficient 1)
      // It should contain one variable term and optionally one coefficient term for it
      const xTerms = xSide.filter(t => t.isVariable || t.isCoefficient);
      if (xTerms.length === 0 || xTerms.length > 2) return false; // Must have x, max 2 terms (coeff + x)
      
      let xCoefficient = 0;
      if (xTerms.length === 1 && xTerms[0].isVariable) {
        xCoefficient = xTerms[0].value;
      } else if (xTerms.length === 2 && xTerms[0].isCoefficient && xTerms[1].isVariable) {
        xCoefficient = xTerms[1].value; // The value of the variable term holds the coefficient
      } else {
        return false; // Malformed x side
      }

      if (xCoefficient !== 1) return false; // x must have a coefficient of 1

      // Value side should only have a single constant term
      const constantTerms = valueSide.filter(t => t.type === 'constant');
      if (constantTerms.length !== 1) return false;
      
      const calculatedValue = constantTerms[0].value;
      
      if (Math.abs(calculatedValue - this.solution) < 0.001) { // Use a small epsilon for float comparison
        this.showSuccess = true;
        this.score += Math.max(100 - this.moves * 5, 10);
        return true;
      }
      
      return false;
    },
    
    checkSolution() {
      if (this.checkIfSolved()) {
        // Already handled in checkIfSolved
      } else {
        this.hint = 'Not quite! Keep moving terms to isolate x.';
        setTimeout(() => this.hint = '', 3000);
      }
    },
    
    resetEquation() {
      this.loadLevel(this.currentLevel);
    },
    
    getHint() {
      const leftHasX = this.leftSide.some(t => t.isVariable);
      const rightHasX = this.rightSide.some(t => t.isVariable);
      const leftHasConstant = this.leftSide.some(t => t.type === 'constant');
      const rightHasConstant = this.rightSide.some(t => t.type === 'constant');
      
      // Get the actual coefficient of x on each side
      const getXCoeff = (side) => {
        let coeff = 0;
        side.forEach(term => {
          if (term.isVariable) {
            coeff += term.value;
          }
        });
        return coeff;
      };

      const leftXCoeff = getXCoeff(this.leftSide);
      const rightXCoeff = getXCoeff(this.rightSide);

      // Get the sum of constants on each side
      const getConstantSum = (side) => {
        let sum = 0;
        side.forEach(term => {
          if (term.type === 'constant') {
            sum += term.value;
          }
        });
        return sum;
      };

      const leftConstantSum = getConstantSum(this.leftSide);
      const rightConstantSum = getConstantSum(this.rightSide);

      if (leftXCoeff !== 0 && rightXCoeff !== 0) {
        this.hint = 'Move all x terms to one side! (e.g., move ' + (rightXCoeff > 0 ? '+' : '') + rightXCoeff + 'x to the left)';
      } else if (leftXCoeff !== 0 && leftConstantSum !== 0) {
        this.hint = 'Move the constant term (' + (leftConstantSum > 0 ? '+' : '') + leftConstantSum + ') to the right side!';
      } else if (rightXCoeff !== 0 && rightConstantSum !== 0) {
        this.hint = 'Move the constant term (' + (rightConstantSum > 0 ? '+' : '') + rightConstantSum + ') to the left side!';
      } else if (leftXCoeff !== 0 && leftXCoeff !== 1) {
        this.hint = 'Drag the coefficient (' + leftXCoeff + ') away from x to divide both sides!';
      } else if (rightXCoeff !== 0 && rightXCoeff !== 1) {
        this.hint = 'Drag the coefficient (' + rightXCoeff + ') away from x to divide both sides!';
      } else {
        this.hint = 'Keep going! You\'re on the right track!';
      }
      
      setTimeout(() => this.hint = '', 5000);
    },
    
    nextLevel() {
      this.currentLevel++;
      this.loadLevel(this.currentLevel);
    }
  }
};
</script>

<style scoped>
.equator-game {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.header {
  text-align: center;
  color: white;
  margin-bottom: 30px;
}

.header h1 {
  font-size: 2.5em;
  margin-bottom: 10px;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
}

.header p {
  font-size: 1.1em;
  opacity: 0.9;
}

.game-content {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

/* Stats Panel */
.stats-panel {
  display: flex;
  justify-content: space-around;
  margin-bottom: 30px;
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 15px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: white;
}

.stat-label {
  font-size: 0.9em;
  opacity: 0.9;
  margin-bottom: 5px;
}

.stat-value {
  font-size: 2em;
  font-weight: bold;
}

/* Equation Workspace */
.equation-workspace {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 40px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-radius: 15px;
  margin-bottom: 30px;
  min-height: 150px;
  flex-wrap: wrap;
}

.workspace-title {
  width: 100%;
  text-align: center;
  font-size: 1.2em;
  font-weight: bold;
  color: #333;
  margin-bottom: 20px;
}

.equation-side {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  min-width: 100px;
  min-height: 60px;
  padding: 10px;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 10px;
  border: 2px dashed rgba(102, 126, 234, 0.3);
}

.term-container {
  transition: all 0.3s ease;
}

.term-container.dragging {
  opacity: 0.5;
}

.equation-term {
  padding: 15px 20px;
  border-radius: 10px;
  font-size: 1.8em;
  font-weight: bold;
  transition: all 0.3s ease;
  user-select: none;
}

.equation-term.draggable {
  cursor: move;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}

.equation-term.draggable:hover {
  transform: translateY(-3px) scale(1.05);
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.2);
}

.equation-term.coefficient {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: white;
}

.equation-term.variable {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  color: white;
}

.equation-term.constant {
  background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
  color: white;
}

.equation-term.operator {
  background: transparent;
  color: #333;
  padding: 10px;
  cursor: default;
  box-shadow: none;
}

.equals-sign {
  font-size: 3em;
  font-weight: bold;
  color: #667eea;
  padding: 0 20px;
}

/* Drop Zones */
.drop-zones {
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
}

.drop-zone {
  flex: 1;
  padding: 30px;
  border: 3px dashed rgba(102, 126, 234, 0.3);
  border-radius: 15px;
  text-align: center;
  transition: all 0.3s ease;
  background: rgba(102, 126, 234, 0.05);
}

.drop-zone.highlight {
  border-color: #667eea;
  background: rgba(102, 126, 234, 0.2);
  transform: scale(1.02);
}

.zone-label {
  font-size: 1.1em;
  color: #667eea;
  font-weight: bold;
}

/* Simplified Display */
.simplified-display {
  text-align: center;
  padding: 20px;
  background: linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%);
  border-radius: 15px;
  margin-bottom: 30px;
}

.simplified-label {
  font-size: 0.9em;
  color: #666;
  margin-bottom: 10px;
}

.simplified-equation {
  font-size: 2em;
  font-weight: bold;
  color: #333;
  font-family: 'Courier New', monospace;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-bottom: 20px;
}

.btn {
  padding: 15px 30px;
  border: none;
  border-radius: 25px;
  font-size: 1em;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}

.btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.2);
}

.btn-check {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-reset {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: white;
}

.btn-hint {
  background: linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%);
  color: #333;
}

.btn-next {
  background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
  color: white;
  padding: 15px 40px;
  font-size: 1.2em;
}

/* Hint Display */
.hint-display {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 15px;
  background: linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%);
  border-radius: 10px;
  margin-top: 20px;
}

.hint-icon {
  font-size: 1.5em;
}

.hint-text {
  font-size: 1.1em;
  color: #333;
  font-weight: 500;
}

/* Success Message */
.success-message {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.success-content {
  background: white;
  padding: 50px;
  border-radius: 20px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.success-icon {
  font-size: 5em;
  margin-bottom: 20px;
}

.success-text {
  font-size: 2em;
  font-weight: bold;
  color: #667eea;
  margin-bottom: 15px;
}

.success-stats {
  font-size: 1.2em;
  color: #666;
  margin-bottom: 30px;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

/* Instructions Panel */
.instructions-panel {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 15px;
  padding: 25px;
  margin-top: 30px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
}

.instructions-panel h3 {
  color: #667eea;
  margin-bottom: 15px;
  font-size: 1.5em;
}

.instructions-panel ul {
  list-style: none;
  padding: 0;
}

.instructions-panel li {
  padding: 10px 0;
  color: #333;
  font-size: 1em;
  line-height: 1.6;
  border-bottom: 1px solid rgba(102, 126, 234, 0.1);
}

.instructions-panel li:last-child {
  border-bottom: none;
}

.instructions-panel strong {
  color: #667eea;
}

/* SEO Content Section */
.seo-content-section {
  margin-top: 30px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 15px;
}

.seo-scrollview {
  height: 60px;
  overflow-y: auto;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  padding: 10px;
}

.seo-content {
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
  font-size: 14px;
}

.seo-content h2 {
  font-size: 18px;
  margin-bottom: 15px;
  color: white;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
}

.seo-content h3 {
  font-size: 16px;
  margin: 20px 0 10px 0;
  color: #e6f3ff;
  font-weight: bold;
}

.seo-content p {
  margin-bottom: 15px;
  text-align: justify;
  opacity: 0.95;
}

/* Responsive Design */
@media (max-width: 768px) {
  .header h1 {
    font-size: 1.8em;
  }
  
  .equation-workspace {
    padding: 20px;
    flex-direction: column;
  }
  
  .equation-term {
    font-size: 1.4em;
    padding: 12px 16px;
  }
  
  .equals-sign {
    font-size: 2em;
    transform: rotate(90deg);
  }
  
  .drop-zones {
    flex-direction: column;
  }
  
  .action-buttons {
    flex-direction: column;
  }
  
  .btn {
    width: 100%;
  }
}
</style>
